import { Extension, mergeAttributes, Node } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { SITE } from '@/app/constants';
import { T2A_SUGGESTIONS } from '../../constant';

interface SuggestionNodeAttrs {
  suggestion: string;
}

export const SuggestionHint = Node.create({
  name: 'suggestionHint',
  group: 'inline',
  inline: true,
  selectable: false,
  atom: true,

  addAttributes() {
    return {
      suggestion: {
        default: '',
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-suggestion-hint]',
      },
    ];
  },

  renderHTML({ node }) {
    return [
      'span',
      mergeAttributes({
        'data-suggestion-hint': '',
        class: 'absolute inline-flex items-center gap-1 select-none text-system-white36 text-sm cursor-text md:hidden',
      }),
      ['span', { class: 'leading-[24px] ' }, node.attrs.suggestion || ''],
      [
        'span',
        {
          class: 'text-xs bg-system-white10 px-2 py-0.5 rounded-6 border border-[rgba(255,255,255,0.16)] leading-[1.4]',
        },
        'tab',
      ],
    ];
  },
});

let currentSuggestionIndex = 0;

const getNextSuggestion = (): string => {
  const suggestion = T2A_SUGGESTIONS[SITE][currentSuggestionIndex];
  currentSuggestionIndex = (currentSuggestionIndex + 1) % T2A_SUGGESTIONS[SITE].length;
  return suggestion;
};

const getNodeAttrs = (node: ProseMirrorNode | null): SuggestionNodeAttrs => {
  if (!node) return { suggestion: '' };
  return node.attrs as unknown as SuggestionNodeAttrs;
};

const getNodeSize = (node: ProseMirrorNode | null): number => {
  if (!node) return 0;
  return typeof node.nodeSize === 'number' ? node.nodeSize : 0;
};

// 查找suggestionHint节点
const findSuggestionHintNode = (doc: ProseMirrorNode, startPos: number, endPos: number) => {
  let found = false;
  let suggestionPos = -1;
  let suggestionNode: ProseMirrorNode | null = null;

  doc.nodesBetween(startPos, endPos, (node, pos) => {
    if (node?.type?.name === 'suggestionHint') {
      found = true;
      suggestionPos = pos;
      suggestionNode = node;
      return false;
    }
    return true;
  });

  return { found, suggestionPos, suggestionNode };
};

const processedTimeTagIds = new Set<string>();

/**
 * Tab智能提示扩展
 * 自动在时间标签后添加建议提示
 * 支持Tab键选择提示
 */
export const TabSuggestionExtension = Extension.create({
  name: 'tabSuggestion',

  addProseMirrorPlugins() {
    const pluginKey = new PluginKey('tabSuggestion');

    return [
      new Plugin({
        key: pluginKey,

        view(editorView) {
          // 点击事件处理函数
          const handleClick = (event: MouseEvent) => {
            let target = event.target as HTMLElement | null;
            let isHintClick = false;

            // 遍历父元素查找data-suggestion-hint属性
            while (target && target !== document.body) {
              if (target?.hasAttribute('data-suggestion-hint')) {
                isHintClick = true;
                break;
              }
              target = target.parentNode as HTMLElement | null;
            }

            if (!isHintClick) return;
            event.preventDefault();
            editorView.focus();

            try {
              const { state } = editorView;

              // 查找所有提示节点
              const positions: number[] = [];
              state.doc.descendants((node, pos) => {
                if (node.type.name === 'suggestionHint') {
                  positions.push(pos);
                }
                return true;
              });

              if (positions.length > 0) {
                // 找到第一个提示节点位置（通常只有一个）
                const hintPos = positions[0];

                // 设置光标位置到提示节点处
                const { TextSelection } = require('@tiptap/pm/state');
                const tr = state.tr;
                tr.setSelection(TextSelection.near(state.doc.resolve(hintPos)));
                editorView.dispatch(tr);
              }
            } catch (e) {
              console.error('设置光标位置失败', e);
            }
          };

          // 处理DOM级别的粘贴，确保在编辑器处理之前删除提示节点
          const handleEditorPaste = (event: ClipboardEvent) => {
            if (!event.clipboardData) return;

            try {
              const { state } = editorView;

              let hasHints = false;
              const positions: number[] = [];

              state.doc.descendants((node, pos) => {
                if (node.type.name === 'suggestionHint') {
                  positions.push(pos);
                  hasHints = true;
                }
                return true;
              });

              if (hasHints && positions.length > 0) {
                const tr = state.tr;

                // 从后向前删除节点，避免位置变化
                for (let i = positions.length - 1; i >= 0; i--) {
                  const pos = positions[i];
                  const node = state.doc.nodeAt(pos);
                  if (node && node.type.name === 'suggestionHint') {
                    tr.delete(pos, pos + node.nodeSize);
                  }
                }
                editorView.dispatch(tr);
              }
            } catch (e) {
              console.error('DOM粘贴处理失败', e);
            }
          };

          editorView.dom.addEventListener('click', handleClick);
          editorView.dom.addEventListener('paste', handleEditorPaste, true);

          return {
            destroy() {
              editorView.dom.removeEventListener('click', handleClick);
              editorView.dom.removeEventListener('paste', handleEditorPaste, true);
            },
          };
        },

        props: {
          handleKeyDown(view, event) {
            const { state } = view;
            const { $from } = state.selection;

            // 根据事件类型确定查找范围
            const searchEnd = event.key === 'Tab' ? $from.end() + 1 : $from.pos;

            // 查找附近的suggestionHint节点
            const { found, suggestionPos, suggestionNode } = findSuggestionHintNode(
              state.doc,
              $from.start(),
              searchEnd,
            );

            if (found && suggestionNode && suggestionPos >= 0) {
              const tr = state.tr;
              const nodeSize = getNodeSize(suggestionNode);

              if (event.key === 'Tab') {
                event.preventDefault();

                const attrs = getNodeAttrs(suggestionNode);
                const suggestion = attrs.suggestion;

                // 完全删除提示节点，然后插入纯文本
                tr.delete(suggestionPos, suggestionPos + nodeSize).insertText(suggestion, suggestionPos);
                view.dispatch(tr);
                return true;
              }

              if (event?.key?.length === 1 && !event.ctrlKey && !event.metaKey) {
                // 普通文本输入 - 删除提示
                tr.delete(suggestionPos, suggestionPos + nodeSize);
                view.dispatch(tr);
              }
            }

            return false;
          },
        },

        appendTransaction(transactions, oldState, newState) {
          // 忽略没有文档变化的事务
          if (!transactions.some((tr) => tr.docChanged)) {
            return null;
          }

          // 检查是否是添加timeTag相关的操作
          const isTimestampAdded = transactions.some((tr) => tr.getMeta('addTimeTag') || tr.getMeta('splitBlock'));
          const isContentChange = transactions.some((tr) => tr.docChanged);

          if (!isTimestampAdded && !isContentChange) return null;

          const { doc } = newState;
          const { doc: oldDoc } = oldState;

          // 收集旧文档中所有timeTag的ID
          const oldTimeTagIds = new Set<string>();
          oldDoc.descendants((node, pos) => {
            if (node?.type?.name === 'timeTag') {
              oldTimeTagIds.add(`${pos}-${node.attrs.id || ''}`);
            }
            return true;
          });

          // 收集当前文档中的所有timeTag信息
          const timeTagInfos: Array<{ pos: number; id: string; isNew: boolean }> = [];
          doc.descendants((node, pos) => {
            if (node?.type?.name === 'timeTag') {
              const nodeId = `${pos}-${node.attrs.id || ''}`;
              const nodeEnd = pos + getNodeSize(node);

              // 收集节点信息：位置、ID和是否新增
              timeTagInfos.push({
                pos: nodeEnd,
                id: nodeId,
                isNew: !oldTimeTagIds.has(nodeId),
              });
            }
            return true;
          });

          // 如果没有timeTag，直接返回
          if (timeTagInfos.length === 0) return null;

          // 是否是首次添加timeTag
          const firstTimeTagAdded = oldTimeTagIds.size === 0 && timeTagInfos.length > 0;

          for (const info of timeTagInfos) {
            // 跳过已处理且非新增的标签
            if (processedTimeTagIds.has(info.id) && !info.isNew) continue;

            // 检查是否需要添加提示
            const shouldAddSuggestion = info.isNew || firstTimeTagAdded || isTimestampAdded;

            if (!shouldAddSuggestion) continue;

            // 检查标签后是否有内容
            let hasContent = false;
            doc.nodesBetween(info.pos, Math.min(info.pos + 100, doc.content.size), (node, nodePos) => {
              // 遇到另一个timeTag停止
              if (node?.type?.name === 'timeTag' && nodePos > info.pos) return false;

              // 检查文本内容
              if (node.isText && node.text && node.text.trim().length > 0) {
                hasContent = true;
                return false;
              }
              return true;
            });

            // 如果有内容，标记为处理过并跳过
            if (hasContent) {
              processedTimeTagIds.add(info.id);
              continue;
            }

            // 检查是否已有提示
            let hasHint = false;
            doc.nodesBetween(info.pos, Math.min(info.pos + 5, doc.content.size), (node) => {
              if (node?.type?.name === 'suggestionHint') {
                hasHint = true;
                return false;
              }
              return true;
            });

            // 如果没有提示，添加一个
            if (!hasHint) {
              try {
                const tr = newState.tr;
                const suggestion = getNextSuggestion();
                const suggestionNode = newState.schema.nodes.suggestionHint.create({ suggestion });
                tr.insert(info.pos, suggestionNode);
                tr.setMeta('addSuggestionHint', true);

                // 标记为已处理
                processedTimeTagIds.add(info.id);
                return tr;
              } catch (error) {
                console.error('添加提示失败:', error);
              }
            } else {
              // 已有提示，标记为已处理
              processedTimeTagIds.add(info.id);
            }
          }

          return null;
        },
      }),
    ];
  },
});
